import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { useAuthStore } from '../../store/authStore';
import { followUser, unfollowUser, isFollowing } from '../../lib/engagementService';
import { supabase } from '../../lib/supabase';

interface FollowButtonProps {
  userId: string;
  username: string;
  onFollowChange?: (isFollowing: boolean) => void;
  size?: 'sm' | 'md' | 'lg';
  isPrivate?: boolean;
}

export const FollowButton: React.FC<FollowButtonProps> = ({
  userId,
  username,
  onFollowChange,
  size = 'sm',
  isPrivate = false
}) => {
  const { user: currentUser } = useAuthStore();
  const [following, setFollowing] = useState(false);
  const [requested, setRequested] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (currentUser && userId !== currentUser.id) {
      checkFollowStatus();
    } else {
      setChecking(false);
    }
  }, [currentUser, userId]);

  const checkFollowStatus = async () => {
    if (!currentUser) return;

    try {
      // Check if already following
      const followStatus = await isFollowing(currentUser.id, userId);
      setFollowing(followStatus);

      // If not following and account is private, check for pending request
      if (!followStatus && isPrivate) {
        const { data, error } = await supabase
          .from('follow_requests')
          .select('id')
          .eq('requester_id', currentUser.id)
          .eq('requested_id', userId)
          .eq('status', 'pending')
          .single();

        if (error && error.code !== 'PGRST116') throw error;
        setRequested(!!data);
      }
    } catch (error) {
      console.error('Error checking follow status:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleToggleFollow = async () => {
    if (!currentUser || loading) return;

    setLoading(true);

    try {
      if (following) {
        // Unfollow
        await unfollowUser(currentUser.id, userId);
        setFollowing(false);
        setRequested(false);
        
        if (onFollowChange) {
          onFollowChange(false);
        }
      } else if (requested) {
        // Cancel request
        const { error } = await supabase
          .from('follow_requests')
          .delete()
          .eq('requester_id', currentUser.id)
          .eq('requested_id', userId)
          .eq('status', 'pending');

        if (error) throw error;
        setRequested(false);
      } else {
        // Follow or request to follow
        if (isPrivate) {
          // Send follow request
          const { error } = await supabase
            .from('follow_requests')
            .insert([{
              requester_id: currentUser.id,
              requested_id: userId,
              status: 'pending'
            }]);

          if (error) throw error;
          setRequested(true);
        } else {
          // Follow directly
          await followUser(currentUser.id, userId);
          setFollowing(true);
          
          if (onFollowChange) {
            onFollowChange(true);
          }
        }
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
      alert('Failed to update follow status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Don't show button for current user
  if (!currentUser || userId === currentUser.id) {
    return null;
  }

  if (checking) {
    return (
      <Button variant="outline" size={size} disabled>
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
      </Button>
    );
  }

  const getButtonText = () => {
    if (following) return 'Following';
    if (requested) return 'Requested';
    return 'Follow';
  };

  const getButtonVariant = () => {
    if (following) return 'outline';
    if (requested) return 'outline';
    return 'primary';
  };

  return (
    <Button
      variant={getButtonVariant()}
      size={size}
      onClick={handleToggleFollow}
      isLoading={loading}
      disabled={loading}
    >
      {getButtonText()}
    </Button>
  );
};